import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Box, 
  Button,
  Typography, 
  TextField, 
  Paper, 
  TableContainer, 
  Table, 
  TableHead, 
  TableRow, 
  TableCell, 
  TableBody, 
  InputAdornment,
  useTheme,
  CircularProgress,
  Alert,
  TablePagination
} from '@mui/material';
import { 
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon
} from '@mui/icons-material';
import { chargingSessionService, whitelistService } from '../services/api';
import { ChargingSessionData } from '../types';
import { formatDateForAPIFrom, formatDateForAPITo } from '../utils/dateUtils';
import { ColumnSelector, ColumnConfig } from '../components/ColumnSelector';

const SESSION_COLUMNS: ColumnConfig[] = [
  { key: 'date', label: 'Date', visible: true },
  { key: 'start_time', label: 'Start Time', visible: true },
  { key: 'duration', label: 'Duration', visible: true },
  { key: 'energy', label: 'Energy', visible: true },
  { key: 'avg_power', label: 'Avg Power', visible: true },
  { key: 'max_power', label: 'Max Power', visible: true },
  { key: 'start_soc', label: 'Battery at start', visible: true },
  { key: 'end_soc', label: 'Battery at end', visible: true },
  { key: 'outlet', label: 'Outlet', visible: true },
  { key: 'rfid', label: 'RFID', visible: true },
  { key: 'name', label: 'Name', visible: true },
  { key: 'customer', label: 'Company', visible: true },
  { key: 'evcc_id', label: 'EVCC ID', visible: true },
];

const EMPHASIZED_COLUMNS = new Set(['energy', 'outlet', 'rfid', 'name', 'customer']);

const ChargerChargingSessions: React.FC = () => {
  const theme = useTheme();
  const { orgId, unitId } = useParams<{ orgId: string; unitId: string }>();
  const [searchTerm, setSearchTerm] = useState('');
  const [chargingSessions, setChargingSessions] = useState<ChargingSessionData[]>([]);
  const [nameByRfid, setNameByRfid] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [columns, setColumns] = useState<ColumnConfig[]>(SESSION_COLUMNS);
  const [columnSelectorOpen, setColumnSelectorOpen] = useState(false);
  const columnButtonRef = useRef<HTMLButtonElement>(null);
  const visibleColumns = columns.filter((column) => column.visible);
  
  // Date range state - default to last week (including today)
  const [dateRange, setDateRange] = useState(() => ({
    start: new Date(Date.now() - 6 * 24 * 60 * 60 * 1000), // 7 days ago (including today = 7 days total)
    end: new Date()
  }));

  // Fetch charging sessions data
  const fetchChargingSessions = useCallback(async (isInitialLoad = false) => {
    if (!orgId || !unitId) {
      setError('Missing organization ID or unit ID');
      setLoading(false);
      return;
    }

    try {
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setDataLoading(true);
      }
      setError(null);
      
      const fromDate = formatDateForAPIFrom(dateRange.start);
      const toDate = formatDateForAPITo(dateRange.end);
      
      console.log('Fetching charger charging sessions for orgId:', orgId, 'unitId:', unitId, 'from:', fromDate, 'to:', toDate);
      const [response, whitelistResponse] = await Promise.all([
        chargingSessionService.getChargingSessions(orgId, unitId, fromDate, toDate),
        whitelistService.getWhitelist(orgId, unitId).catch((whitelistError) => {
          console.error('Error fetching whitelist:', whitelistError);
          return null;
        }),
      ]);
      if (response.success) {
        // Handle both possible response structures
        const sessions = response.data.sessions || response.data.data || [];
        setChargingSessions(sessions);
        const names: Record<string, string> = {};
        (whitelistResponse?.data?.entries || []).forEach((entry) => {
          const rfid = (entry.rfid || '').trim().toLowerCase();
          const name = (entry.user_name || '').trim();
          if (rfid && name && !names[rfid]) {
            names[rfid] = name;
          }
        });
        setNameByRfid(names);
      } else {
        setError('Failed to fetch charging sessions');
      }
    } catch (err) {
      console.error('Error fetching charging sessions:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch charging sessions');
    } finally {
      if (isInitialLoad) {
        setLoading(false);
      } else {
        setDataLoading(false);
      }
    }
  }, [orgId, unitId, dateRange.start, dateRange.end]);

  useEffect(() => {
    fetchChargingSessions(true); // Initial load
  }, [orgId, unitId, fetchChargingSessions]);

  useEffect(() => {
    if (loading === false) { // Only fetch when initial load is complete
      fetchChargingSessions(false); // Data refresh
    }
  }, [dateRange.start, dateRange.end, fetchChargingSessions, loading]);

  const searchText = (value: unknown) => (typeof value === 'string' ? value : '').toLowerCase();
  const sortText = (value: unknown) => (typeof value === 'string' ? value : '');

  const getSessionName = (rfid?: string | null) => {
    if (typeof rfid !== 'string' || !rfid.trim()) return '';
    return nameByRfid[rfid.trim().toLowerCase()] || '';
  };

  const filteredData = (chargingSessions || [])
    .filter(session =>
      searchText(session.customer).includes(searchTerm.toLowerCase()) ||
      getSessionName(session.rfid).toLowerCase().includes(searchTerm.toLowerCase()) ||
      searchText(session.unit_name).includes(searchTerm.toLowerCase()) ||
      searchText(session.rfid).includes(searchTerm.toLowerCase()) ||
      searchText(session.date).includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      // Sort by date first (descending)
      const dateA = a.date ? new Date(a.date).getTime() : 0;
      const dateB = b.date ? new Date(b.date).getTime() : 0;
      
      if (dateB !== dateA) {
        return dateB - dateA; // Descending order
      }
      
      // If dates are the same, sort by start_time (descending)
      const timeA = sortText(a.start_time);
      const timeB = sortText(b.start_time);
      
      // For time strings in HH:MM:SS format, direct string comparison works
      // For descending order (later times first): if timeB > timeA, return positive so timeB comes first
      if (timeB > timeA) return 1;  // timeB is later, should come first
      if (timeB < timeA) return -1;  // timeB is earlier, should come later
      return 0; // times are equal
    });

  const toFiniteNumber = (value: unknown): number | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    if (typeof value === 'string' && value.trim() !== '') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  };

  const displayText = (value: unknown) => {
    if (typeof value === 'string') return value || 'N/A';
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
    return 'N/A';
  };

  const formatEnergy = (energy: unknown) => {
    const value = toFiniteNumber(energy);
    return value === null ? 'N/A' : `${value.toFixed(1)} kWh`;
  };

  const formatPower = (power: unknown) => {
    const value = toFiniteNumber(power);
    return value === null ? 'N/A' : `${value.toFixed(1)} kW`;
  };

  const formatSoC = (soc: unknown) => {
    const value = toFiniteNumber(soc);
    return value === null ? 'N/A' : `${Math.round(value)} %`;
  };

  const formatDuration = (duration: unknown) => {
    const value = toFiniteNumber(duration);
    if (value === null) return 'N/A';
    const hours = Math.floor(value / 60);
    const minutes = value % 60;
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  const formatTime = (time: unknown, date: unknown) => {
    if (typeof time !== 'string' || !time) return 'N/A';
    
    try {
      let utcDate: Date;
      
      // Check if time is already a full datetime string (contains 'T' or is ISO format)
      if (time.includes('T') || time.includes('Z') || time.includes('+')) {
        // It's already a full datetime string, parse it directly
        utcDate = new Date(time);
      } else if (typeof date === 'string' && date) {
        // Combine date and time to create a UTC datetime string
        // Assuming date is in YYYY-MM-DD format and time is in HH:MM:SS format (both UTC)
        const utcDateTimeString = `${date}T${time}Z`;
        utcDate = new Date(utcDateTimeString);
      } else {
        // No date available, return original time
        return time;
      }
      
      // Check if date is valid
      if (isNaN(utcDate.getTime())) {
        return time; // Invalid date, return original
      }
      
      // Convert to Oslo timezone (Europe/Oslo)
      const osloTime = utcDate.toLocaleTimeString('en-GB', {
        timeZone: 'Europe/Oslo',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      return osloTime;
    } catch (error) {
      console.error('Error formatting time:', error);
      return time; // Fallback to original time if conversion fails
    }
  };

  // Format date for input value
  const formatDateForInput = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getCellText = (session: ChargingSessionData, key: string) => {
    try {
      switch (key) {
        case 'date':
          return displayText(session.date);
        case 'start_time':
          return formatTime(session.start_time, session.date);
        case 'duration':
          return formatDuration(session.duration);
        case 'energy':
          return formatEnergy(session.energy);
        case 'avg_power':
          return formatPower(session.avg_power);
        case 'max_power':
          return formatPower(session.max_power);
        case 'start_soc':
          return formatSoC(session.start_soc);
        case 'end_soc':
          return formatSoC(session.end_soc);
        case 'outlet':
          return displayText(session.outlet);
        case 'rfid':
          return displayText(session.rfid);
        case 'name':
          return getSessionName(session.rfid) || 'N/A';
        case 'customer':
          return displayText(session.customer);
        case 'evcc_id':
          return displayText(session.evcc_id);
        default:
          return '';
      }
    } catch (error) {
      console.error('Error rendering charging session cell:', error);
      return 'N/A';
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, backgroundColor: '#FAFAFA', p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'flex-start', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
          Charging Sessions
        </Typography>
      </Box>
      <Typography variant="body1" sx={{ color: theme.palette.text.secondary, mb: 4 }}>
        This is the Charging Sessions page. Here you can view and manage charging session data.
      </Typography>

      {/* Date Range and Search Section */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', alignItems: 'center', flexWrap: 'wrap', mb: 3, gap: 2 }}>
        {/* Date Range */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ position: 'relative' }}>
            <input
              type="date"
              value={formatDateForInput(dateRange.start)}
              onChange={(e) => {
                if (e.target.value) {
                  setDateRange(prev => ({ ...prev, start: new Date(e.target.value) }));
                }
              }}
              style={{
                width: '150px',
                height: '40px',
                padding: '8px 12px',
                fontSize: '16px',
                fontFamily: 'Montserrat',
                border: '1px solid rgba(0,0,0,0.23)',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                color: 'rgba(0,0,0,0.87)',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1976d2';
                e.target.style.borderWidth = '2px';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(0,0,0,0.23)';
                e.target.style.borderWidth = '1px';
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: '-8px',
                left: '12px',
                backgroundColor: '#FAFAFA',
                padding: '0 4px',
                fontSize: '12px',
                color: 'rgba(0,0,0,0.6)',
                fontFamily: 'Montserrat',
              }}
            >
              Start Date
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', fontFamily: 'Montserrat' }}>
            –
          </Typography>
          <Box sx={{ position: 'relative' }}>
            <input
              type="date"
              value={formatDateForInput(dateRange.end)}
              onChange={(e) => {
                if (e.target.value) {
                  setDateRange(prev => ({ ...prev, end: new Date(e.target.value) }));
                }
              }}
              style={{
                width: '150px',
                height: '40px',
                padding: '8px 12px',
                fontSize: '16px',
                fontFamily: 'Montserrat',
                border: '1px solid rgba(0,0,0,0.23)',
                borderRadius: '4px',
                backgroundColor: 'transparent',
                color: 'rgba(0,0,0,0.87)',
                outline: 'none',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#1976d2';
                e.target.style.borderWidth = '2px';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = 'rgba(0,0,0,0.23)';
                e.target.style.borderWidth = '1px';
              }}
            />
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                top: '-8px',
                left: '12px',
                backgroundColor: '#FAFAFA',
                padding: '0 4px',
                fontSize: '12px',
                color: 'rgba(0,0,0,0.6)',
                fontFamily: 'Montserrat',
              }}
            >
              End Date
            </Typography>
          </Box>
        </Box>

        {/* Search Field */}
        <TextField
          placeholder="Search by name, company, unit name, RFID, or date..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(0); // Reset to first page when searching
          }}
          size="small"
          sx={{ 
            width: 400,
            '& .MuiOutlinedInput-root': {
              height: '40px',
              borderRadius: '4px',
            },
            '& .MuiInputBase-input': {
              height: '40px',
              padding: '0 14px',
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: theme.palette.text.secondary }} />
              </InputAdornment>
            ),
          }}
        />

        <Button
          ref={columnButtonRef}
          variant="text"
          startIcon={<ViewColumnIcon />}
          onClick={() => setColumnSelectorOpen((open) => !open)}
          sx={{ color: '#337A5C' }}
        >
          Columns
        </Button>

        <ColumnSelector
          columns={columns}
          onColumnsChange={setColumns}
          open={columnSelectorOpen}
          onClose={() => setColumnSelectorOpen(false)}
          anchorEl={columnButtonRef.current}
        />
      </Box>

      {/* Charging Sessions Table */}
      <TableContainer component={Paper} sx={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', position: 'relative' }}>
        {dataLoading && (
          <Box sx={{ 
            position: 'absolute', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            backgroundColor: 'rgba(255, 255, 255, 0.8)', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            zIndex: 1 
          }}>
            <CircularProgress />
          </Box>
        )}
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              {visibleColumns.map((column) => (
                <TableCell key={column.key} sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((session, index) => (
              <TableRow key={`${session.id ?? 'session'}-${index}`} sx={{ '&:hover': { backgroundColor: '#f8f9fa' } }}>
                {visibleColumns.map((column) => (
                  <TableCell key={column.key}>
                    <Typography
                      variant="body2"
                      sx={{ color: EMPHASIZED_COLUMNS.has(column.key) ? theme.palette.text.primary : theme.palette.text.secondary }}
                    >
                      {getCellText(session, column.key)}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={filteredData.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{ 
          borderTop: '1px solid rgba(0,0,0,0.12)',
          '& .MuiTablePagination-toolbar': {
            paddingLeft: 0,
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-selectLabel': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.6)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-displayedRows': {
            fontFamily: 'Montserrat',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.4px',
            lineHeight: '1.66'
          },
          '& .MuiTablePagination-select': {
            fontFamily: 'Roboto',
            fontSize: '12px',
            color: 'rgba(0,0,0,0.87)',
            letterSpacing: '0.3px',
            lineHeight: '19px'
          }
        }}
      />

      {/* No results message */}
      {filteredData.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="body1" color="text.secondary">
            No charging sessions found matching your search criteria.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default ChargerChargingSessions;
